import React, {
  lazy,
  Suspense,
  useState,
  useContext,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useReducer, // Added useReducer
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  useMediaQuery,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  StepIcon,
  StepConnector,
  Button,
  Snackbar,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Paper,
  Divider,
  Fade,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useAuth } from "../util/auth";
import { FormContext } from "./FormContext";
import { formSchema } from "./FormSection5_TargetJobInformation";
import FormSectionHeader from "./FormSectionHeader";
import GenerateApplicationMaterialsButton from "./GenerateApplicationMaterialsButton";
import GeneratedContentDisplay from "./GeneratedContentDisplay";
import { db } from "../util/firebase";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import ErrorBoundary from "./ErrorBoundary";
import SidebarToggleIcon from "./SidebarToggleIcon";
import useSticky from "../hooks/useSticky";
import ContextualCreditsDisplay from "./ContextualCreditsDisplay";

import TargetJobInformation from "./FormSection5_TargetJobInformation"; // Import directly

// --- Lazy Loaded Components ---
const PersonalInformation = lazy(() =>
  import("./FormSection1_PersonalInformation")
);
const WorkExperience = lazy(() => import("./FormSection3_WorkExperience"));
const Education = lazy(() => import("./FormSection2_Education"));
// const TargetJobInformation = lazy(() => import("./FormSection5_TargetJobInformation")); // Keep commented out
const AdditionalQualifications = lazy(() =>
  import("./FormSection4_AdditionalQualifications")
);

// --- Constants ---
const SCROLL_THRESHOLD = 150;
const FORM_STEPS = [
  { title: "Personal Information", component: PersonalInformation },
  { title: "Education History", component: Education },
  { title: "Work Experience", component: WorkExperience },
  { title: "Additional Qualifications", component: AdditionalQualifications },
  { title: "Target Job Information", component: TargetJobInformation },
];
const TOTAL_STEPS = FORM_STEPS.length;

// --- UI State Management ---
const initialUiState = {
  isLoading: false,
  formAlert: null,
  snackbarMessage: null,
};

function uiReducer(state, action) {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_FORM_ALERT":
      return { ...state, formAlert: action.payload, snackbarMessage: null };
    case "SET_SNACKBAR":
      return { ...state, snackbarMessage: action.payload, formAlert: null };
    case "CLEAR_ALERTS":
      return { ...state, formAlert: null, snackbarMessage: null };
    case "RESET":
      return initialUiState;
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

// --- Custom Styles ---
const ColorlibConnector = withStyles((theme) => ({
  alternativeLabel: { top: 18 },
  active: {
    "& $line": {
      backgroundImage: `linear-gradient(to right, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    },
  },
  completed: {
    "& $line": {
      backgroundImage: `linear-gradient(to right, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    },
  },
  line: {
    height: 3,
    border: 0,
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[700]
        : theme.palette.grey[300],
    borderRadius: 1,
    transition: "all 0.5s ease",
  },
}))(StepConnector);

const useColorlibStepIconStyles = makeStyles((theme) => ({
  root: {
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[700]
        : theme.palette.grey[300],
    zIndex: 1,
    color: theme.palette.text.secondary,
    width: 34, // Reduced from 40
    height: 34, // Reduced from 40
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
    transition: "all 0.3s ease",
    boxShadow: theme.shadows[1],
  },
  active: {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: `0 4px 8px 0 rgba(${theme.palette.primary.main
      .replace("#", "")
      .match(/../g)
      .map((hex) => parseInt(hex, 16))
      .join(", ")}, 0.25)`,
    color: theme.palette.primary.contrastText,
  },
  completed: {
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    color: theme.palette.primary.contrastText,
  },
  text: { fontSize: "0.85rem", fontWeight: "bold" }, // Reduced from 1rem
  checkIcon: { fontSize: "1.2rem" }, // Reduced from 1.5rem
}));

function ColorlibStepIcon(props) {
  const classes = useColorlibStepIconStyles();
  const { active, completed, icon } = props;
  return (
    <div
      className={`${classes.root} ${active ? classes.active : ""} ${
        completed ? classes.completed : ""
      }`}
    >
      {completed ? (
        <CheckCircleIcon className={classes.checkIcon} />
      ) : (
        <Typography className={classes.text}>{icon}</Typography>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  formContainer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3, 3, 2), // Reduced padding, especially at the bottom
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(3), // Reduced bottom margin
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundImage:
      theme.palette.type === "dark"
        ? "linear-gradient(to bottom, rgba(66, 66, 66, 0.4) 0%, rgba(25, 25, 25, 0.2) 100%)"
        : "linear-gradient(to bottom, rgba(255, 255, 255, 0.8) 0%, rgba(245, 245, 245, 0.6) 100%)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.05)"
    }`,
    transition: "all 0.3s ease",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
  },
  formContent: { position: "relative", padding: theme.spacing(1, 0) }, // Reduced vertical padding
  alertBox: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  navigationButtonsTop: {
    position: "relative", // Changed from absolute to relative
    display: "flex",
    justifyContent: "space-between",
    zIndex: 1,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(3), // Add margin to create space between buttons and content
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  stepper: {
    padding: theme.spacing(1, 0),
    backgroundColor: "transparent",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.5, 0),
      marginBottom: theme.spacing(0.5),
    },
  },
  stepLabel: {
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": { transform: "translateY(-2px)" },
  },
  stepLabelText: {
    fontWeight: 600,
    marginTop: theme.spacing(0.5), // Reduced from 1
    fontSize: "0.85rem", // Added smaller font size
    transition: "all 0.2s ease",
  },
  stepLabelActive: { color: theme.palette.primary.main },
  stepLabelCompleted: { color: theme.palette.primary.main },
  generateButtonContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(4),
    position: "relative",
    // "&::before": { // Commented out to remove the top line
    //   content: '""',
    //   position: "absolute",
    //   top: -theme.spacing(3),
    //   left: "25%",
    //   right: "25%",
    //   height: 1,
    //   backgroundColor:
    //     theme.palette.type === "dark"
    //       ? "rgba(255, 255, 255, 0.1)"
    //       : "rgba(0, 0, 0, 0.1)",
    // },
  },
  generatedContentContainer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(4),
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.05)"
    }`,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    transition: "all 0.3s ease",
    zIndex: 999,
  },
  materialContainer: { marginBottom: theme.spacing(4) },
  generationsButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1, 2), // Reduced horizontal padding for more compact appearance
    minWidth: "auto", // Changed from fixed 70px to auto
    minHeight: "40px",
    borderRadius: 25,
    transition: "all 0.2s ease",
    "&:hover": { transform: "translateY(-2px)", boxShadow: theme.shadows[4] },
  },
  generationsIcon: {
    fontSize: "1.5rem",
    margin: theme.spacing(0, 1), // Reduced margin from 4 to 1 for more compact appearance
  },
  tabContainer: {
    transition: "all 0.3s ease",
    backgroundColor: theme.palette.background.paper,
    zIndex: 1000,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    overflow: "hidden",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.05)"
    }`,
    borderBottom: "none",
    boxShadow: theme.shadows[2],
    marginBottom: theme.spacing(0.5), // Add a small gap between tabs and content
  },
  tabContainerSticky: {
    position: "sticky",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
    transition: "all 0.3s ease",
    zIndex: 1000,
  },
  tabContainerNormal: { position: "relative", height: "auto" },
  tab: {
    fontSize: "1rem",
    fontWeight: 600,
    minHeight: 48, // Reduced height
    letterSpacing: "0.5px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.03)",
    },
    "&$selected": {
      color:
        theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.primary.main,
      fontWeight: 700,
      // Clean, subtle background for selected tab
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(0, 0, 0, 0.02)",
    },
    "@media (max-width:428px)": { fontSize: "0.9rem" },
    cursor: "pointer",
    textTransform: "none", // Use sentence case instead of all caps
  },
  selected: {
    // No special border, let the indicator do the work
    position: "relative",
    zIndex: 1,
  },
  snackbar: { bottom: theme.spacing(2) },
  countsDisplay: { marginBottom: theme.spacing(2) },
  navigationButton: {
    borderRadius: 25,
    padding: theme.spacing(1, 3),
    transition: "all 0.2s ease",
    "&:hover": { transform: "translateY(-2px)", boxShadow: theme.shadows[3] },
  },
  backButton: {
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[800]
        : theme.palette.grey[200],
    color: theme.palette.text.primary,
  },
  nextButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  sectionDivider: { margin: theme.spacing(3, 0), width: "100%" },
}));

// --- Component ---
function JobDetailsForm({
  generations,
  onAddGenerations,
  onUpdateGenerationContent,
  selectedGeneration,
  selectedGenerationId,
  onSelectGeneration,
  handleDrawerToggle,
  selectedApplicationMaterial,
  onSelectApplicationMaterial,
  activeTab,
  setActiveTab,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const auth = useAuth();
  const { formData } = useContext(FormContext);
  const [uiState, dispatchUi] = useReducer(uiReducer, initialUiState);
  const [currentStep, setCurrentStep] = useState(0);
  const [userCredits, setUserCredits] = useState(0);
  const contentRef = useRef(null);

  const hasPastGenerations = useMemo(
    () => Array.isArray(generations) && generations.length > 0,
    [generations]
  );
  const { isSticky, navbarHeight } = useSticky(SCROLL_THRESHOLD);

  // --- Navigation Callbacks ---
  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const savePersonalInformation = useCallback(async () => {
    if (!auth.user) return;
    try {
      const localData = localStorage.getItem("personalInformation");
      const currentData = formData.personalInformation;
      let dataChanged = true;
      if (localData) {
        const parsedLocalData = JSON.parse(localData);
        dataChanged =
          JSON.stringify(parsedLocalData) !== JSON.stringify(currentData);
      }
      if (dataChanged) {
        const userDocRef = doc(db, "users", auth.user.uid);
        await setDoc(
          userDocRef,
          { personalInformation: currentData },
          { merge: true }
        );
        dispatchUi({
          type: "SET_SNACKBAR",
          payload: "Personal information saved.",
        });
        localStorage.setItem(
          "personalInformation",
          JSON.stringify(currentData)
        );
      }
    } catch (error) {
      console.error("Error saving Personal Information to Firestore:", error);
      dispatchUi({
        type: "SET_FORM_ALERT",
        payload: { type: "error", message: "Error saving personal data." },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user, formData.personalInformation, dispatchUi]);

  const handleNext = useCallback(async () => {
    if (currentStep === 0) {
      await savePersonalInformation();
    }
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep((prev) => prev + 1);
    } else if (hasPastGenerations) {
      handleDrawerToggle();
    }
  }, [
    currentStep,
    hasPastGenerations,
    handleDrawerToggle,
    savePersonalInformation,
  ]);

  // --- Step Navigation ---
  const handleStepClick = (step) => () => {
    setCurrentStep(step);
  };

  // --- Tab Management ---
  const handleTabChange = useCallback(
    (event, newValue) => {
      setActiveTab(newValue);
      if (newValue === 1 && !selectedGenerationId && hasPastGenerations) {
        const mostRecentGeneration = generations[generations.length - 1];
        onSelectGeneration(mostRecentGeneration.id);
      }
    },
    [
      setActiveTab,
      onSelectGeneration,
      selectedGenerationId,
      generations,
      hasPastGenerations,
    ]
  );

  // --- Rendering Logic ---
  const renderSection = useCallback(() => {
    const StepComponent = FORM_STEPS[currentStep].component;
    const isTargetJobStep = currentStep === 4; // Index of TargetJobInformation

    // Conditionally wrap in Suspense
    const componentToRender = isTargetJobStep ? (
      <StepComponent /> // Render directly if it's TargetJobInformation
    ) : (
      <Suspense fallback={<div>Loading...</div>}>
        <StepComponent />
      </Suspense>
    );

    return <ErrorBoundary>{componentToRender}</ErrorBoundary>;
  }, [currentStep]);

  // --- Content Generation ---
  const handleGenerate = useCallback(async () => {
    try {
      dispatchUi({ type: "CLEAR_ALERTS" });
      await formSchema.validate(formData, { abortEarly: false });
      dispatchUi({ type: "SET_LOADING", payload: true });
    } catch (validationErrors) {
      let message = "Please fix the validation errors before proceeding.";
      if (validationErrors.inner && validationErrors.inner.length > 0) {
        console.error("Validation errors:", validationErrors.inner);
        message = `Validation Error: ${validationErrors.inner[0].message}`;
      }
      dispatchUi({
        type: "SET_FORM_ALERT",
        payload: { type: "warning", message },
      });
      dispatchUi({ type: "SET_LOADING", payload: false });
    }
  }, [formData, dispatchUi]);

  const progress = useMemo(
    () => ((currentStep + 1) / TOTAL_STEPS) * 100,
    [currentStep]
  );

  // --- Effects ---
  useEffect(() => {
    let unsubscribe;
    const listenToUserCredits = () => {
      if (auth.user) {
        const userDocRef = doc(db, "users", auth.user.uid);
        unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            setUserCredits(Number(data.credits) || 0);
          } else {
            setUserCredits(5);
          }
        });
      } else {
        setUserCredits(0);
      }
    };
    listenToUserCredits();
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [auth.user]);

  const handleContentGeneratedInternal = useCallback(
    (allResults) => {
      dispatchUi({ type: "SET_LOADING", payload: false });
      setActiveTab(1);
      const targetJobs = formData.targetJobInformation || [];
      const newGenerations = allResults.map((res, index) => {
        const job = targetJobs[index] || {};
        const companyName = job.companyName || "Unknown Company";
        const jobTitle = job.jobTitle || "Unknown Job";
        const generationId = `${Date.now()}-${index}`;
        return {
          id: generationId,
          companyName,
          jobTitle,
          ...res,
          jobDetails: job,
        };
      });
      onAddGenerations(newGenerations);
      if (newGenerations.length > 0) {
        onSelectGeneration(newGenerations[0].id);
      }
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    },
    [
      setActiveTab,
      formData.targetJobInformation,
      onAddGenerations,
      onSelectGeneration,
      dispatchUi,
    ]
  );

  // Calculate total selections using useMemo for efficiency
  const totalSelections = useMemo(() => {
    const targetJobInfo = formData.targetJobInformation;
    if (!Array.isArray(targetJobInfo)) {
      return 0;
    }
    return targetJobInfo.reduce((total, job) => {
      const options = job.generationOptions || {};
      const selectedCount = Object.values(options).filter(Boolean).length;
      return total + selectedCount;
    }, 0);
  }, [formData.targetJobInformation]);

  // --- Render ---
  return (
    <Box>
      {/* Sticky Tab Header */}
      <Paper
        elevation={isSticky ? 4 : 1}
        className={`${classes.tabContainer} ${
          isSticky ? classes.tabContainerSticky : classes.tabContainerNormal
        }`}
        style={isSticky ? { top: `${navbarHeight}px` } : {}}
      >
        {/* <Box className={classes.countsDisplay}>
          <ContextualCreditsDisplay
            showUpgradePrompt={true}
            totalSelections={totalSelections} // Use the memoized value
          />
        </Box> */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="Application Builder and Generated Content Tabs"
          TabIndicatorProps={{
            style: {
              height: 4,
              borderRadius: "4px 4px 0 0",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            },
          }}
        >
          <Tab
            label="Application Builder"
            classes={{ root: classes.tab, selected: classes.selected }}
            id="tab-application-builder"
            aria-controls="tabpanel-application-builder"
          />
          <Tab
            label="Your Tailored Documents"
            disabled={!hasPastGenerations}
            classes={{ root: classes.tab, selected: classes.selected }}
            id="tab-generated-content"
            aria-controls="tabpanel-generated-content"
          />
        </Tabs>
      </Paper>

      {/* Tab Panel 0: Application Builder */}
      {activeTab === 0 && (
        <Fade in={activeTab === 0} timeout={300}>
          <Paper className={classes.formContainer}>
            {/* Combined Stepper and Navigation Buttons (Desktop Only) */}
            {!isMobile && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
                position="relative"
              >
                <Button
                  variant="contained"
                  className={`${classes.navigationButton} ${classes.backButton}`}
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  aria-label="Back"
                  startIcon={<ArrowBackIcon />}
                  size="small"
                  style={{ position: "relative", top: "-30px" }} // Move only the back button higher
                >
                  Back
                </Button>

                <Stepper
                  activeStep={currentStep}
                  alternativeLabel
                  className={classes.stepper}
                  nonLinear // Allows clicking on steps
                  connector={<ColorlibConnector />}
                >
                  {FORM_STEPS.map((step, index) => (
                    <Step key={index} completed={currentStep > index}>
                      <StepLabel
                        StepIconComponent={ColorlibStepIcon}
                        onClick={handleStepClick(index)}
                        className={classes.stepLabel}
                        classes={{
                          label: `${classes.stepLabelText} ${
                            currentStep === index ? classes.stepLabelActive : ""
                          } ${
                            currentStep > index
                              ? classes.stepLabelCompleted
                              : ""
                          }`,
                        }}
                      >
                        {step.title}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Tooltip
                  title={
                    currentStep === TOTAL_STEPS - 1 && hasPastGenerations
                      ? "View Past Application Materials"
                      : "Proceed to Next Step"
                  }
                  arrow
                  placement="top"
                >
                  <span>
                    <Button
                      variant="contained"
                      className={`${classes.navigationButton} ${
                        classes.nextButton
                      } ${
                        currentStep === TOTAL_STEPS - 1 && hasPastGenerations
                          ? classes.generationsButton
                          : ""
                      }`}
                      onClick={handleNext}
                      disabled={
                        currentStep === TOTAL_STEPS - 1 && !hasPastGenerations
                      }
                      aria-label={
                        currentStep === TOTAL_STEPS - 1 && hasPastGenerations
                          ? "View Past Application Materials"
                          : "Next"
                      }
                      endIcon={
                        currentStep === TOTAL_STEPS - 1 &&
                        hasPastGenerations ? (
                          <SidebarToggleIcon
                            className={classes.generationsIcon}
                            aria-hidden="true"
                          />
                        ) : (
                          <ArrowForwardIcon />
                        )
                      }
                      size="small"
                      style={{ position: "relative", top: "-30px" }} // Move only the next button higher
                    >
                      {currentStep === TOTAL_STEPS - 1 && hasPastGenerations
                        ? "View Materials"
                        : "Next"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}

            {/* Section Header (Mobile Only) */}
            {isMobile && (
              <FormSectionHeader
                title={FORM_STEPS[currentStep].title}
                progress={progress}
                isMobile={isMobile}
                onBack={handleBack}
                onNext={handleNext}
                isLastStep={currentStep === TOTAL_STEPS - 1}
              />
            )}

            {/* Form Content Area */}
            <Box className={classes.formContent}>
              {/* Render Current Form Section */}
              <Fade in={true} timeout={500}>
                <Box>{renderSection()}</Box>
              </Fade>

              {/* Form Alert Display (uses uiState.formAlert) */}
              {uiState.formAlert && (
                <Box className={classes.alertBox}>
                  <Alert
                    severity={uiState.formAlert.type}
                    onClose={() => dispatchUi({ type: "CLEAR_ALERTS" })} // Clear alert on close
                    aria-live="assertive"
                    variant="filled"
                    elevation={3}
                  >
                    {uiState.formAlert.message}
                  </Alert>
                </Box>
              )}

              {/* Generate Button (Only on Last Step) */}
              {currentStep === TOTAL_STEPS - 1 && (
                <>
                  {/* <Divider className={classes.sectionDivider} />  Removed this divider */}
                  <Box className={classes.generateButtonContainer}>
                    <GenerateApplicationMaterialsButton
                      onContentGenerated={handleContentGeneratedInternal}
                      onGenerateStart={handleGenerate}
                      isLoading={uiState.isLoading} // Use isLoading from reducer state
                    />
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Tab Panel 1: Generated Content */}
      {activeTab === 1 && (
        <Fade in={activeTab === 1} timeout={300}>
          <Paper
            className={classes.generatedContentContainer}
            ref={contentRef}
            role="tabpanel"
            id="tabpanel-generated-content"
            aria-labelledby="tab-generated-content"
            elevation={3}
          >
            {selectedGeneration ? (
              <Fade in={!!selectedGeneration} timeout={500}>
                <Box className={classes.materialContainer}>
                  {typeof selectedGeneration === "object" ? (
                    <GeneratedContentDisplay
                      isVisible
                      generatedContent={selectedGeneration}
                      jobDetails={{
                        jobTitle: selectedGeneration.jobTitle,
                        companyName: selectedGeneration.companyName,
                      }}
                      updateGeneratedContent={(key, newValue) => {
                        const updatedContent = {
                          ...selectedGeneration,
                          [key]: newValue,
                        };
                        onUpdateGenerationContent(
                          selectedGeneration.id,
                          updatedContent
                        );
                      }}
                      isLoading={uiState.isLoading} // Pass isLoading state from reducer
                      generationOptions={
                        selectedGeneration.jobDetails?.generationOptions // Use optional chaining
                      }
                      selectedApplicationMaterial={selectedApplicationMaterial}
                      onSelectApplicationMaterial={onSelectApplicationMaterial}
                      scrollToATSAnalysis={() => {
                        const atsAnalysisElement =
                          document.getElementById("ats-analysis");
                        if (atsAnalysisElement) {
                          atsAnalysisElement.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="body1" color="error">
                      Error: Generated content is not in the expected format.
                    </Typography>
                  )}
                </Box>
              </Fade>
            ) : hasPastGenerations ? (
              <Typography
                variant="body1"
                align="center"
                style={{ padding: theme.spacing(4) }}
              >
                No generation selected. Please select one from the sidebar or
                generate new materials.
              </Typography>
            ) : (
              <Typography
                variant="body1"
                align="center"
                style={{ padding: theme.spacing(4) }}
              >
                You haven't generated any application materials yet. Fill out
                the form and click "Generate".
              </Typography>
            )}
          </Paper>
        </Fade>
      )}

      {/* Consolidated Snackbar for Feedback (uses uiState.snackbarMessage and uiState.formAlert) */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={!!uiState.snackbarMessage || !!uiState.formAlert} // Open if either message exists
        autoHideDuration={uiState.formAlert ? 6000 : 4000} // Longer duration for alerts/warnings
        onClose={() => dispatchUi({ type: "CLEAR_ALERTS" })} // Clear both on close
        TransitionComponent={Fade}
        className={classes.snackbar}
      >
        <Alert
          onClose={() => dispatchUi({ type: "CLEAR_ALERTS" })}
          // Determine severity based on which message is present
          severity={
            uiState.formAlert
              ? uiState.formAlert.type // Use alert type (error, warning)
              : "success" // Default to success for snackbar messages
          }
          variant="filled"
          aria-live={uiState.formAlert ? "assertive" : "polite"} // More assertive for alerts
          elevation={6}
        >
          {/* Show the appropriate message */}
          {uiState.formAlert
            ? uiState.formAlert.message
            : uiState.snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

JobDetailsForm.propTypes = {
  generations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      companyName: PropTypes.string.isRequired,
      jobTitle: PropTypes.string.isRequired,
      jobDetails: PropTypes.object.isRequired,
      // Add other expected fields if necessary
    })
  ),
  onAddGenerations: PropTypes.func.isRequired,
  onUpdateGenerationContent: PropTypes.func.isRequired,
  // onContentGenerated: PropTypes.func.isRequired, // Keep this prop? Seems unused now.
  selectedGeneration: PropTypes.shape({
    id: PropTypes.string.isRequired,
    companyName: PropTypes.string.isRequired,
    jobTitle: PropTypes.string.isRequired,
    jobDetails: PropTypes.object, // Can be optional if generation happens before details are fully set
    // Add other expected fields
  }),
  selectedGenerationId: PropTypes.string,
  onSelectGeneration: PropTypes.func.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
  selectedApplicationMaterial: PropTypes.string,
  onSelectApplicationMaterial: PropTypes.func.isRequired,
  activeTab: PropTypes.number.isRequired,
  setActiveTab: PropTypes.func.isRequired,
};

JobDetailsForm.defaultProps = {
  generations: [],
  selectedGeneration: null,
  selectedGenerationId: null,
  selectedApplicationMaterial: "",
  // onContentGenerated: () => {}, // Provide a default no-op function if keeping prop
};

export default React.memo(JobDetailsForm);
